import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  durationStringToMinutes,
  ServiceGroup,
  ServiceEventType,
  TeamEventMeetingType,
} from '@vendasta/meetings';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { durationMinutes } from '../shared/duration.utils';
import { MAX_BATCH_EVENTS, isEventUnavailable } from '../util';
import { GroupServicesDialogComponent } from './group-services-dialog/group-services-dialog.component';
import { RemoveServiceDialogComponent } from './remove-service-dialog/remove-service-dialog.component';
import { CartItem } from '../shared/booking-cart/booking-cart.component';

type FlowType = 'service' | 'group' | 'calendar';

@Component({
  selector: 'app-scheduling-page',
  standalone: false,
  templateUrl: './scheduling-page.component.html',
  styleUrls: ['./scheduling-page.component.scss'],
})
export class SchedulingPageComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly storeService = inject(BookMeNowStoreService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  searchQuery = signal('');
  loading = signal(true);
  loadError = signal(false);

  /** Which booking flow is active based on the route */
  flowType = signal<FlowType>('service');

  /** Group heading — shown only for group routes */
  groupName = signal('');
  groupDescription = signal('');

  groups = signal<ServiceGroup[]>([]);
  eventTypes = signal<ServiceEventType[]>([]);

  filteredGroups = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.groups();
    }
    return this.groups().filter(
      (g) =>
        g.name?.toLowerCase().includes(query) ||
        g.description?.toLowerCase().includes(query) ||
        g.eventTypes?.some(
          (e) =>
            e.name?.toLowerCase().includes(query) ||
            e.description?.toLowerCase().includes(query)
        )
    );
  });

  filteredEventTypes = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.eventTypes();
    }
    return this.eventTypes().filter(
      (e) =>
        e.name?.toLowerCase().includes(query) ||
        e.description?.toLowerCase().includes(query)
    );
  });

  /** Whether multi-service cart mode is enabled (from Service.isBatchBookingEnabled) */
  isBatchBookingEnabled = signal(false);

  selectedEvents = signal<ServiceEventType[]>([]);

  /** Tracks which group each selected event came from (eventId → groupId) */
  private eventSourceGroupMap = new Map<string, string>();


  /** Set of selected event IDs for cross-group dedup in group dialogs */
  selectedEventIds = computed(
    () => new Set(this.selectedEvents().map((e) => e.id))
  );

  isMaxEventsReached = computed(
    () =>
      this.isBatchBookingEnabled() &&
      this.selectedEvents().length >= MAX_BATCH_EVENTS
  );

  /** When batch booking is off and something is selected, track which group it came from */
  hasNonBatchSelection = computed(
    () => !this.isBatchBookingEnabled() && this.selectedEvents().length > 0
  );

  isEventUnavailable(event: ServiceEventType): boolean {
    return isEventUnavailable(event);
  }

  isStandaloneEventDisabled(event: ServiceEventType): boolean {
    // Already selected via a group — grey out in additional services
    if (this.isEventSelected(event) && this.eventSourceGroupMap.has(event.id)) {
      return true;
    }
    if (this.isMaxEventsReached() && !this.isEventSelected(event)) {
      return true;
    }
    if (!this.hasNonBatchSelection()) {
      return false;
    }
    return !this.isEventSelected(event);
  }

  cartItems = computed<CartItem[]>(() =>
    this.selectedEvents().map((event) => ({
      name: event.name,
      initials: this.getInitials(event.name),
      providerName: 'Any available',
      duration: event.duration
        ? durationStringToMinutes({ duration: event.duration })
        : 0,
    }))
  );

  cartCtaLabel = computed(() => {
    const selected = this.selectedEvents();
    if (selected.length) {
      return this.needsProviderSelection(selected) ? 'Choose provider' : 'Choose date & time';
    }
    const allEvents = [
      ...this.eventTypes(),
      ...this.groups().flatMap((g) => g.eventTypes || []),
    ];
    if (this.needsProviderSelection(allEvents)) {
      return 'Choose provider';
    }
    const hasClientChoice = allEvents.some((e) => {
      const type = e.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
      return type === TeamEventMeetingType.CLIENT_CHOICE;
    });
    return hasClientChoice ? 'Choose provider' : 'Choose date & time';
  });


  ngOnInit(): void {
    const serviceId = this.route.snapshot.params.serviceId;
    const serviceSlug = this.route.snapshot.params.serviceSlug;
    const groupId = this.route.snapshot.params['groupId'];
    const groupSlug = this.route.snapshot.params['groupSlug'];
    const calendarId = this.route.snapshot.params['calendarId'];

    // Restore selections from store before any load (loadServiceV2 clears them)
    const savedEvents = this.storeService.getSelectedBookingEvents();
    const savedGroupMap = this.storeService.getEventSourceGroupMap();

    if (serviceId && serviceSlug) {
      this.flowType.set('service');
      this.storeService.setBookingOriginId(serviceId);
      this.storeService
        .loadServiceV2({ serviceId })
        .pipe(
          catchError((err) => {
            this.storeService.handleMeetingTypeError(err);
            return of(null);
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((response) => {
          if (response) {
            if (
              response.bookingUrl &&
              !response.bookingUrl.includes(serviceSlug)
            ) {
              this.storeService.setErrorDisplay('slug-mismatch-error');
              this.loading.set(false);
              return;
            }
            this.isBatchBookingEnabled.set(
              response.isBatchBookingEnabled ?? false
            );
            const groups = (response.groups || []).map((g) => ({
              ...g,
              eventTypes: (g.eventTypes || []).map((e) => this.toServiceEventType(e)),
            }));
            this.groups.set(groups as any);
            this.eventTypes.set(
              (response.eventTypes || []).map((e) => this.toServiceEventType(e))
            );
          }
          this.loading.set(false);
          if (savedEvents.length) {
            this.selectedEvents.set(savedEvents);
            this.eventSourceGroupMap = savedGroupMap;
          }
          this.updateProviderStepFlag();
        });
    } else if (groupId && groupSlug) {
      this.flowType.set('group');
      this.storeService.setBookingOriginId(groupId);
      this.loadGroup(groupId, groupSlug, savedEvents);
    } else if (calendarId) {
      this.flowType.set('calendar');
      this.storeService.setBookingOriginId(calendarId);
      this.loadCalendarMeetingTypes(calendarId, savedEvents);
    } else {
      this.loading.set(false);
      this.loadError.set(true);
    }
  }

  onGroupClick(group: ServiceGroup): void {
    const isMultiSelect =
      this.isBatchBookingEnabled() && (group.isBatchBookingEnabled ?? false);

    const preSelectedIds = new Set<string>();
    for (const eventId of this.selectedEventIds()) {
      if (this.eventSourceGroupMap.get(eventId) === group.id) {
        preSelectedIds.add(eventId);
      }
    }

    // When batch is off and selection exists from a different group,
    // disable all events in this group's dialog
    const disableAll =
      !this.isBatchBookingEnabled() &&
      this.selectedEvents().length > 0 &&
      preSelectedIds.size === 0;

    const disabledIds = new Set<string>();
    if (disableAll) {
      // Disable all events in this group
      (group.eventTypes || []).forEach((e) => disabledIds.add(e.id));
    } else {
      for (const eventId of this.selectedEventIds()) {
        const sourceGroupId = this.eventSourceGroupMap.get(eventId);
        if (sourceGroupId !== group.id) {
          disabledIds.add(eventId);
        }
      }
    }

    const dialogRef = this.dialog.open(GroupServicesDialogComponent, {
      data: {
        group,
        isMultiSelect,
        disabledEventIds: disabledIds,
        preSelectedEventIds: preSelectedIds,
        serviceBatchBookingEnabled: this.isBatchBookingEnabled(),
        maxEvents: MAX_BATCH_EVENTS,
        totalSelectedCount: this.selectedEvents().length,
        preSelectedFromGroupCount: preSelectedIds.size,
      },
      width: '463px',
      maxWidth: '90vw',
      maxHeight: '594px',
      panelClass: 'group-services-dialog-panel',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ServiceEventType | ServiceEventType[]) => {
        if (!result) {
          return;
        }
        // Remove previously selected events from this group before applying new selection
        const previousGroupEventIds = [...this.selectedEventIds()].filter(
          (id) => this.eventSourceGroupMap.get(id) === group.id
        );
        previousGroupEventIds.forEach((id) => this.removeEventFromCart(id));

        const events = Array.isArray(result) ? result : [result];
        events.forEach((event) => {
          this.eventSourceGroupMap.set(event.id, group.id);
          this.addEventToCart(event, group.id);
        });
      });
  }

  onInlineEventToggle(event: ServiceEventType): void {
    if (this.isEventUnavailable(event)) return;
    if (this.isEventDisabled(event)) return;

    if (this.isEventSelected(event)) {
      this.selectedEvents.update((current) =>
        current.filter((e) => e.id !== event.id)
      );
    } else {
      if (!this.isBatchBookingEnabled()) {
        this.selectedEvents.set([event]);
      } else {
        this.selectedEvents.update((current) => [...current, event]);
      }
    }
    this.syncSelectedEventsToStore();
    this.updateProviderStepFlag();
  }

  onStandaloneEventToggle(event: ServiceEventType): void {
    if (this.isEventUnavailable(event)) return;
    if (this.flowType() === 'service') {
      if (this.isEventSelected(event)) {
        this.removeEventFromCart(event.id);
      } else {
        this.addEventToCart(event);
      }
    } else {
      this.selectedEvents.set([event]);
      this.onChooseProvider();
    }
  }

  isEventDisabled(event: ServiceEventType): boolean {
    if (this.isEventSelected(event)) return false;
    if (this.isMaxEventsReached()) return true;
    if (!this.isBatchBookingEnabled() && this.selectedEvents().length > 0)
      return true;
    return false;
  }

  isEventSelected(event: ServiceEventType): boolean {
    return this.selectedEvents().some((e) => e.id === event.id);
  }

  onRemoveCartItem(index: number): void {
    const event = this.selectedEvents()[index];
    if (!event) {
      return;
    }

    const dialogRef = this.dialog.open(RemoveServiceDialogComponent, {
      data: { serviceName: event.name },
      width: '540px',
      panelClass: 'remove-service-dialog-panel',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.removeEventFromCart(event.id);
        }
      });
  }

  durationMinutes = durationMinutes;

  getInitials(name: string): string {
    if (!name) {
      return '';
    }
    return name
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
  }

  onChooseProvider(): void {
    if (this.flowType() === 'service') {
      this.storeService.setSelectedBookingEvents(this.selectedEvents());
      this.storeService.setEventSourceGroupMap(this.eventSourceGroupMap);
    } else {
      this.syncSelectedEventsToStore();
    }

    const needsProvider = this.needsProviderSelection(this.selectedEvents());
    this.storeService.setProviderStepSkipped(!needsProvider);
    this.storeService.setBookingStep(needsProvider ? 'provider' : 'datetime');
  }

  private needsProviderSelection(events: ServiceEventType[]): boolean {
    return events.some((event) => {
      const type = event.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
      if (type !== TeamEventMeetingType.CLIENT_CHOICE) {
        return false;
      }
      const allHosts = event.hostUser || [];
      const hasConnected = allHosts.some((h: any) => h.isConnected === true);
      const connectedProviders = hasConnected ? allHosts.filter((h: any) => h.isConnected === true) : allHosts;
      return connectedProviders.length > 1;
    });
  }

  getServiceCountBadge(group: ServiceGroup): string {
    const count = group.eventTypes?.length ?? 0;
    if (!count) {
      return '';
    }
    return `${count} service${count !== 1 ? 's' : ''}`;
  }

  private addEventToCart(event: ServiceEventType, groupId?: string): void {
    if (!this.isBatchBookingEnabled()) {
      this.eventSourceGroupMap.clear();
      if (groupId) {
        this.eventSourceGroupMap.set(event.id, groupId);
      }
      this.selectedEvents.set([event]);
      this.updateProviderStepFlag();
      return;
    }
    const current = this.selectedEvents();
    const alreadyExists = current.some((e) => e.id === event.id);
    if (alreadyExists || current.length >= MAX_BATCH_EVENTS) {
      return;
    }
    this.selectedEvents.update((list) => [...list, event]);
    this.updateProviderStepFlag();
  }

  private removeEventFromCart(eventId: string): void {
    this.eventSourceGroupMap.delete(eventId);
    this.selectedEvents.update((current) =>
      current.filter((e) => e.id !== eventId)
    );
    this.updateProviderStepFlag();
  }

  private updateProviderStepFlag(): void {
    const allEvents = [
      ...this.eventTypes(),
      ...this.groups().flatMap((g) => g.eventTypes || []),
    ];
    if (this.needsProviderSelection(allEvents)) {
      this.storeService.setShouldShowProviderStep(true);
    } else {
      const hasClientChoice = allEvents.some((e) => {
        const type = e.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
        return type === TeamEventMeetingType.CLIENT_CHOICE;
      });
      this.storeService.setShouldShowProviderStep(hasClientChoice);
    }

    const selected = this.selectedEvents();
    if (selected.length) {
      this.storeService.setProviderStepSkipped(!this.needsProviderSelection(selected));
    } else {
      this.storeService.setProviderStepSkipped(false);
    }
  }

  private loadGroup(groupId: string, groupSlug: string, savedEvents: ServiceEventType[]): void {
    this.storeService
      .loadGroupV2({ groupId })
      .pipe(
        catchError((err) => {
          this.storeService.handleMeetingTypeError(err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((group) => {
        if (group) {
          if (group.bookingUrl && !group.bookingUrl.includes(groupSlug)) {
            this.storeService.setErrorDisplay('slug-mismatch-error');
            this.loading.set(false);
            return;
          }
          this.groupName.set(group.name || '');
          this.groupDescription.set(group.description || '');
          this.isBatchBookingEnabled.set(group.isBatchBookingEnabled ?? false);
          this.eventTypes.set((group.eventTypes || []).map((e) => this.toServiceEventType(e)));
        }
        this.loading.set(false);
        this.restoreValidSelections(savedEvents);
      });
  }

  private loadCalendarMeetingTypes(
    calendarId: string,
    savedEvents: ServiceEventType[]
  ): void {
    this.storeService
      .loadMeetingTypes({ calendarId, calendarSlug: calendarId })
      .pipe(
        catchError((err) => {
          this.storeService.handleMeetingTypeError(err);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((meetingTypes) => {
        if (meetingTypes) {
          this.eventTypes.set(
            meetingTypes.map(
              (mt) =>
                ({
                  id: mt.id,
                  name: mt.name,
                  description: mt.description,
                  duration: mt.duration,
                  calendarId,
                  eventType: mt.meetingType ?? TeamEventMeetingType.ROUND_ROBIN,
                  hostUser: mt.hostUsers,
                  locationType: mt.locationType,
                  location: mt.location,
                  locationGuideline: mt.locationGuidelines,
                  notificationType: mt.notificationType,
                  smsRequired: mt.isPhoneNumberRequired,
                  serviceForm: mt.form || undefined,
                } as unknown as ServiceEventType)
            )
          );
          // Calendar flow is always single-select
          this.isBatchBookingEnabled.set(false);
        }
        this.loading.set(false);
        this.restoreValidSelections(savedEvents);
      });
  }

  /** Restore saved selections only if they exist in the currently loaded event types.
   * Re-maps saved events against the loaded (and potentially enriched) set by id so
   * restored selections carry all enriched fields (smsRequired, notificationType, etc.).
   */
  private restoreValidSelections(savedEvents: ServiceEventType[]): void {
    if (savedEvents.length) {
      const loadedById = new Map(this.eventTypes().map((e) => [e.id, e]));
      const valid = savedEvents
        .filter((e) => loadedById.has(e.id))
        .map((e) => loadedById.get(e.id) as ServiceEventType);
      if (valid.length) {
        this.selectedEvents.set(valid);
      }
    }
    this.updateProviderStepFlag();
  }

  /**
   * Normalize a raw association/meeting-type object to ServiceEventType.
   * Uses spread to preserve all fields (emailRequired, smsRequired,
   * notificationType, locationType, serviceForm, etc.) for downstream pages.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private toServiceEventType(e: any): ServiceEventType {
    let hosts = e.hostUsers || e.hostUser || [];
    if (hosts.length === 0 && e.creatorUserName) {
      hosts = [
        {
          userId: e.identifier || e.id,
          displayName: e.creatorUserName,
          isConnected: true,
        },
      ];
    }
    const meetingType =
      e.teamMeetingType ??
      e.teamEventType ??
      e.eventType ??
      TeamEventMeetingType.ROUND_ROBIN;
    return {
      ...e,
      eventType: meetingType,
      hostUser: hosts,
      locationGuideline: e.locationGuideline ?? e.locationGuidelines,
    } as unknown as ServiceEventType;
  }

  private syncSelectedEventsToStore(): void {
    this.storeService.setSelectedBookingEvents(
      this.selectedEvents().map((e) => this.toServiceEventType(e))
    );
  }
}
